import type { Passenger } from '@aviata/booking-data';

import type { PassengerWithDocType } from '../types';

const getPaxId = (pax: Passenger): string | undefined =>
  (pax as PassengerWithDocType).id;

/**
 * Two passengers are "the same" only when they share a non-empty id or a
 * non-empty document number. Empty values are skipped so newly-created
 * passengers that haven't been filled in yet aren't collapsed into each other.
 *
 * Use this for storage-side dedup (saved-list, sessionStorage upserts).
 * For active-row identity checks where two empty rows should match — e.g.
 * "is the row being edited the same as the one on the card" — use
 * `isSameActivePassenger` instead.
 */
export const isSamePersistedPassenger = (
  left: Passenger,
  right: Passenger,
): boolean => {
  const leftId = getPaxId(left);
  const rightId = getPaxId(right);
  if (leftId && rightId && leftId === rightId) {
    return true;
  }

  const leftDoc = left.document.number;
  const rightDoc = right.document.number;
  return Boolean(leftDoc) && leftDoc === rightDoc;
};

/**
 * Null-safe identity check used to decide whether two passenger references
 * point at the same row (e.g. "is the row the user is editing the one on
 * the card?"). Differs from `isSamePersistedPassenger` in two ways:
 *
 *   1. Either side may be null — returns false in that case.
 *   2. Two empty document numbers count as "the same" — when both the card
 *      and the edited row are placeholder/empty passengers, treating them
 *      as equal lets edits propagate to the card.
 *
 * Do NOT use this for storage dedup — it would collapse unrelated empty
 * rows into a single entry.
 */
export const isSameActivePassenger = (
  left: Passenger | null,
  right: Passenger | null,
): boolean => {
  if (!left || !right) {
    return false;
  }
  const leftId = getPaxId(left);
  const rightId = getPaxId(right);
  if (leftId && rightId) {
    return leftId === rightId;
  }
  return left.document.number === right.document.number;
};
